import { pointGpsRetenu } from './pointsGps';

/**
 * Le tracé d'un camion se lit dans l'ordre du temps.
 *
 * Le serveur rend l'historique du plus récent au plus ancien; le flux temps
 * réel, lui, ajoute à la fin. Mélanger les deux, puis couper « les 100
 * derniers » et retourner la liste, faisait sauter le tracé: les points en
 * direct se retrouvaient avant les anciens. Tout passe donc par ici, qui
 * range chronologiquement (du plus ancien au plus récent) selon l'horodatage.
 */

/** Horodatage en millisecondes, ou `NaN` quand il manque ou est illisible. */
export const horodatagePoint = (point) => {
  const valeur = point?.timestamp;
  if (valeur == null || valeur === '') return NaN;
  return typeof valeur === 'number' ? valeur : Date.parse(valeur);
};

const cle = (point) =>
  `${point.timestamp ?? ''}|${point.latitude}|${point.longitude}`;

/** Garde plus de points qu'on n'en demande au serveur, pour le direct. */
export const TRACE_MAX_POINTS = 1000;

/**
 * Réunit des relevés (historique du serveur, points reçus en direct) en une
 * seule liste chronologique, sans doublon, bornée aux `max` plus récents.
 * Un relevé sans horodatage lisible garde sa place d'arrivée.
 */
export function fusionnerTrace(existants, nouveaux, max = TRACE_MAX_POINTS) {
  const vus = new Set();
  const tous = [];
  [...(existants || []), ...(nouveaux || [])].forEach((point) => {
    if (!pointGpsRetenu(point)) return;
    const k = cle(point);
    if (vus.has(k)) return;
    vus.add(k);
    tous.push(point);
  });
  const ordonnes = tous
    .map((point, rang) => ({ point, rang, t: horodatagePoint(point) }))
    .sort((a, b) => {
      if (Number.isNaN(a.t) || Number.isNaN(b.t) || a.t === b.t) return a.rang - b.rang;
      return a.t - b.t;
    })
    .map(({ point }) => point);
  return ordonnes.length > max ? ordonnes.slice(ordonnes.length - max) : ordonnes;
}

/** Le relevé le plus récent d'une trace chronologique. */
export const dernierPointTrace = (trace) =>
  Array.isArray(trace) && trace.length > 0 ? trace[trace.length - 1] : null;

/** Le plus récent de deux relevés (l'un ou l'autre peut manquer). */
export function pointLePlusRecent(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  const ta = horodatagePoint(a);
  const tb = horodatagePoint(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return b;
  return tb >= ta ? b : a;
}

/** Les coordonnées `[lat, lng]` d'une trace, dans l'ordre du temps. */
export const positionsTrace = (trace) =>
  (trace || []).filter(pointGpsRetenu).map((p) => [p.latitude, p.longitude]);

/** Un relevé de moins de 15 minutes: le camion émet encore. */
export const RELEVE_RECENT_MS = 15 * 60 * 1000;
export const releveRecent = (point, maintenant = Date.now()) => {
  const t = horodatagePoint(point);
  return !Number.isNaN(t) && maintenant - t <= RELEVE_RECENT_MS;
};
